import { useEffect, useRef, useState, type TouchEvent } from "react";

type UseResponsiveCarouselOptions = {
  cardWidth: number;
  gap: number;
  maxPerPage: number;
  itemCount: number;
};

export function useResponsiveCarousel({
  cardWidth,
  gap,
  maxPerPage,
  itemCount,
}: UseResponsiveCarouselOptions) {
  const [containerW, setContainerW] = useState(0);
  const [page, setPage] = useState(0);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // ResizeObserver
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    if (typeof ResizeObserver === "undefined") {
      setContainerW(Math.round(el.getBoundingClientRect().width));
      return;
    }

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setContainerW(Math.round(w));
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Calcoli layout
  const perPage = containerW
    ? Math.max(
        1,
        Math.min(
          maxPerPage,
          Math.floor((containerW + gap) / (cardWidth + gap)),
        ),
      )
    : 1;

  const pageCount = Math.max(1, Math.ceil(itemCount / perPage));

  useEffect(() => {
    setPage((prev) => {
      if (prev > pageCount - 1) return pageCount - 1;
      if (prev < 0) return 0;
      return prev;
    });
  }, [pageCount]);

  const effectiveCardWidth =
    containerW > 0
      ? perPage === 1
        ? Math.min(cardWidth, Math.max(280, containerW - gap * 2))
        : cardWidth
      : Math.min(cardWidth, 320);

  const trackWidth = perPage * effectiveCardWidth + (perPage - 1) * gap;

  // Swipe
  const touchState = useRef({
    startX: 0,
    startY: 0,
    dx: 0,
    dy: 0,
    swiping: false,
  });

  const SWIPE_THRESHOLD = 40;
  const LOCK_AXIS_THRESHOLD = 10;

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    touchState.current = {
      startX: t.clientX,
      startY: t.clientY,
      dx: 0,
      dy: 0,
      swiping: false,
    };
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    const dx = t.clientX - touchState.current.startX;
    const dy = t.clientY - touchState.current.startY;

    touchState.current.dx = dx;
    touchState.current.dy = dy;

    if (!touchState.current.swiping) {
      if (Math.abs(dx) > LOCK_AXIS_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        touchState.current.swiping = true;
      }
    }

    if (touchState.current.swiping) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    const { dx, swiping } = touchState.current;
    if (!swiping) return;

    if (dx <= -SWIPE_THRESHOLD && page < pageCount - 1) {
      setPage((prev) => Math.min(pageCount - 1, prev + 1));
    } else if (dx >= SWIPE_THRESHOLD && page > 0) {
      setPage((prev) => Math.max(0, prev - 1));
    }
  };

  return {
    wrapperRef,
    page,
    setPage,
    perPage,
    pageCount,
    cardWidth: effectiveCardWidth,
    trackWidth,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}